import { useState, useCallback, useMemo, createContext, useContext } from 'react'

const STORE_GROUP_KEY = 'kss-store-groups'

function loadStoreGroups() {
  try {
    const stored = localStorage.getItem(STORE_GROUP_KEY)
    if (stored) return JSON.parse(stored)
  } catch { /* ignore */ }
  return []
}

function saveStoreGroups(groups) {
  localStorage.setItem(STORE_GROUP_KEY, JSON.stringify(groups))
}

const FilterSortContext = createContext()

export function FilterSortProvider({ children }) {
  const value = useFilterSortInternal()
  return <FilterSortContext.Provider value={value}>{children}</FilterSortContext.Provider>
}

function useFilterSortInternal() {
  const [activeStoreGroup, setActiveStoreGroup] = useState(null)
  const [storeGroups, setStoreGroups] = useState(loadStoreGroups)

  const [sortState, setSortState] = useState({
    key: null,
    direction: 'asc',
  })

  const [filters, setFilters] = useState({
    search: '',
    selections: {},
    numberBounds: {},
    dateBounds: {},
    specialCases: {},
  })

  // Store group management
  const createStoreGroup = useCallback((name, stores) => {
    setStoreGroups(prev => {
      const next = [...prev, { id: crypto.randomUUID(), name, stores }]
      saveStoreGroups(next)
      return next
    })
  }, [])

  const updateStoreGroup = useCallback((id, name, stores) => {
    setStoreGroups(prev => {
      const next = prev.map(g => g.id === id ? { ...g, name, stores } : g)
      saveStoreGroups(next)
      return next
    })
  }, [])

  const deleteStoreGroup = useCallback((id) => {
    setStoreGroups(prev => {
      const next = prev.filter(g => g.id !== id)
      saveStoreGroups(next)
      return next
    })
    setActiveStoreGroup(prev => prev?.id === id ? null : prev)
  }, [])

  const selectStoreGroup = useCallback((group) => {
    setActiveStoreGroup(group)
  }, [])

  const clearStoreGroup = useCallback(() => {
    setActiveStoreGroup(null)
  }, [])

  // Sort
  const setSort = useCallback((key, direction) => {
    setSortState({ key, direction: direction || 'asc' })
  }, [])

  const toggleSort = useCallback((key) => {
    setSortState(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }, [])

  const clearSort = useCallback(() => {
    setSortState({ key: null, direction: 'asc' })
  }, [])

  // Filter
  const setSearchFilter = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }))
  }, [])

  const setSelectionFilter = useCallback((key, values) => {
    setFilters(prev => ({
      ...prev,
      selections: { ...prev.selections, [key]: values },
    }))
  }, [])

  const setNumberBound = useCallback((key, lower, upper) => {
    setFilters(prev => ({
      ...prev,
      numberBounds: { ...prev.numberBounds, [key]: { lower, upper } },
    }))
  }, [])

  const setDateBound = useCallback((key, start, end) => {
    setFilters(prev => ({
      ...prev,
      dateBounds: { ...prev.dateBounds, [key]: { start, end } },
    }))
  }, [])

  const setSpecialCaseFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      specialCases: { ...prev.specialCases, [key]: value },
    }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      selections: {},
      numberBounds: {},
      dateBounds: {},
      specialCases: {},
    })
  }, [])

  const clearAll = useCallback(() => {
    clearFilters()
    clearSort()
    clearStoreGroup()
  }, [clearFilters, clearSort, clearStoreGroup])

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.search ||
      Object.keys(filters.selections).length > 0 ||
      Object.keys(filters.numberBounds).length > 0 ||
      Object.keys(filters.dateBounds).length > 0 ||
      Object.keys(filters.specialCases).length > 0 ||
      activeStoreGroup
    )
  }, [filters, activeStoreGroup])

  return {
    sortState,
    filters,
    activeStoreGroup,
    storeGroups,
    hasActiveFilters,

    setSort,
    toggleSort,
    clearSort,
    setSearchFilter,
    setSelectionFilter,
    setNumberBound,
    setDateBound,
    setSpecialCaseFilter,
    clearFilters,
    clearAll,

    createStoreGroup,
    updateStoreGroup,
    deleteStoreGroup,
    selectStoreGroup,
    clearStoreGroup,
  }
}

export function useFilterSort() {
  const ctx = useContext(FilterSortContext)
  if (!ctx) throw new Error('useFilterSort must be used within FilterSortProvider')
  return ctx
}
